import React, { useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Button from 'yoda-core-components/lib/components/Button';
import { bool, string } from 'prop-types';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { cmsCompHeadlineStyle, cmsCompSubHeadlineStyle } from '../../helpers/CMSUtils/cmsStyles';
import Carousel from '../Carousel';
import CMImageRenderer from '../CMImageRenderer/CMImageRenderer';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';
import {
    commonAutomationIds,
    bannerAutomationIds,
    ctaAutomationIds,
} from '../CoreMediaPage/CMPage.config';
import RevieveModal from '../Revieve/RevieveModal';

export const NucleusConfig = {
    nucleusBlockWidth: {
        'nucleus-2': 'nucleus2GridStyle',
        'nucleus-3': 'nucleus3GridStyle',
        'nucleus-4': 'nucleus4GridStyle',
        'nucleus-grid': 'nucleus4GridStyle',
    },
    nucleusContainer: {
        'nucleus-2': 'nucleus2BlockStyle',
        'nucleus-3': 'nucleus3BlockStyle',
        'nucleus-4': 'nucleus4BlockStyle',
        'nucleus-grid': 'nucleus4BlockStyle',
    },
    padding: {
        S: `${dt(['px-1', 'pb-4'])}`,
        M: `${dt(['px-2', 'pb-4'])}`,
        L: `${dt(['px-spacing-4', 'pb-spacing-4'])}`,
        None: `${dt(['px-0', 'pb-4'])}`,
    },
    paddingAccentColor: {
        S: `${dt(['mb-1'])}`,
        M: `${dt(['mb-1'])}`,
        L: `${dt(['mb-1'])}`,
        None: `${dt(['mb-1'])}`,
    },
    nucleusMargin: {
        S: '-mx-1',
        M: '-mx-2',
        L: '-mx-spacing-4',
        None: '-mx-0',
    },
};

const renderImages = (props, isCMPreview) => {
    const [imageHeight, setImageHeight] = useState(0);
    const imagesDom = [];
    const imageStyle = get(props, 'styles.itemStyles.itemPictureShape', 'square');
    const imageAspectRatio = get(
        props,
        'styles.itemStyles.itemPictureAspectRatio',
        'portrait_ratio1x1'
    );
    const componentContainerBackgroundColor = `#${get(
        props,
        'styles.componentContainerBackgroundColor',
        ''
    )}`;
    props?.items?.forEach((item, i) => {
        const altToolTipText = get(item, 'altText', '');
        const altText = get(item, 'media[0].alt', '');
        const imageURL = get(item, 'media[0].uriTemplate', '');
        const href = get(item, 'urlHolder.value', '');
        const imgTarget = get(item, 'urlHolder.openInNewTab', false) ? '_blank' : '';
        const itemTitle = get(item, 'teaserTitle', '');
        const itemSubtitle = get(item, 'teaserSubtitle', '');
        const itemTitleColor = `#${get(props, 'styles.itemStyles.itemHeadlineColor', '')}`;
        const itemSubTitleColor = `#${get(props, 'styles.itemStyles.itemSubHeadlineColor', '')}`;
        const analyticsId = get(item, 'analyticsId', '');
        const itemPadding = get(
            NucleusConfig,
            `padding.${props?.styles?.itemStyles?.itemPadding || 'M'}`
        );
        const bannerTitleStyle = dt(['text-center', 'font-bold', 'text-black', 'text-xlarge']);
        const bannerSubTitleStyle = dt([
            'flex',
            'justify-center',
            'text-center',
            'font-normal',
            'text-black',
            'text-normal',
        ]);
        const nucleusBlockStyle =
            props.componentType === 'nucleus-carousel'
                ? dt(['pr-4', 'pl-0', 'pb-0'])
                : dt([itemPadding]);
        const imageToRender = (
            <CMImageRenderer
                imageURL={imageURL || ''}
                analyticsId={analyticsId}
                automationId={bannerAutomationIds?.IMAGE}
                altText={altText || ''}
                componentType={props.componentType}
                imageAspectRatio={imageAspectRatio}
                itemPictureShape={imageStyle}
                imageStyles={dt(['mb-3'])}
                isCMPreview={isCMPreview}
                data={props}
                setImageHeight={
                    props.componentType === 'nucleus-carousel' && i === 0 ? setImageHeight : null
                }
            />
        );
        const nucleusContainerWidth = get(
            NucleusConfig,
            `nucleusBlockWidth.${props.componentType}`
        );

        let nucleusContainerStyle = '';

        switch (nucleusContainerWidth) {
            case 'nucleus2GridStyle':
                nucleusContainerStyle = dt(['lg:w-1/2', 'sm:w-full', 'self-start']);
                break;
            case 'nucleus3GridStyle':
                nucleusContainerStyle = dt(['lg:w-1/3', 'sm:w-full', 'self-start']);
                break;
            case 'nucleus4GridStyle':
                nucleusContainerStyle = dt(['lg:w-1/4', 'md:w-1/4', 'sm:w-1/2', 'self-start']);
                break;
            default:
                nucleusContainerStyle = '';
                break;
        }
        const imageTitleContainer = dt(['flex', 'self-end', 'flex-col', 'justify-center']);
        const banner = (
            <div
                className={[nucleusBlockStyle, nucleusContainerStyle].join(' ')}
                data-automation-id={bannerAutomationIds?.BANNER}
            >
                <a
                    role="link"
                    aria-label="nucleus-banner"
                    data-automation-id={bannerAutomationIds?.LINK}
                    href={href}
                    target={imgTarget}
                    title={altToolTipText || ''}
                >
                    <RevieveModal
                        data={analyticsId}
                        isCoremedia
                        componentToBeRendered={imageToRender}
                    />
                    <div className={imageTitleContainer}>
                        {itemTitle ? (
                            <div
                                className={bannerTitleStyle}
                                style={{ color: itemTitleColor }}
                                data-automation-id={bannerAutomationIds?.TITLE}
                            >
                                {itemTitle}
                            </div>
                        ) : null}
                        {itemSubtitle ? (
                            <div
                                className={bannerSubTitleStyle}
                                style={{ color: itemSubTitleColor }}
                                data-automation-id={bannerAutomationIds?.SUBTITLE}
                            >
                                {itemSubtitle}
                            </div>
                        ) : null}
                    </div>
                </a>
            </div>
        );
        imagesDom.push(banner);
    });
    if (props.componentType === 'nucleus-carousel') {
        const isBGWhite =
            componentContainerBackgroundColor === '#' ||
            componentContainerBackgroundColor === '#None' ||
            componentContainerBackgroundColor === '#ffffff' ||
            componentContainerBackgroundColor === '#fff';
        const paddingNucleusCarouselStyle = isBGWhite
            ? ''
            : dt(['sm:!w-auto', 'md:-mx-6', 'lg:pl-4', 'mdOnly:pl-10']);
        const wrapperStyles = isBGWhite
            ? dt(['smOnly:-mr-6', 'smOnly:!w-auto', 'smOnly:-ml-6'])
            : dt(['smOnly:!w-auto', 'smOnly:-mx-6']);
        return (
            <Carousel
                theme="nucleus-carousel"
                children={imagesDom}
                automationId={bannerAutomationIds?.CAROUSEL}
                wrapperClass={wrapperStyles}
                contentWrapperClass={paddingNucleusCarouselStyle}
                carouselImageHeight={imageHeight}
                sliderItemContainerStyle={dt(['smOnly:pl-6', 'smOnly:scroll-pl-6'])}
            />
        );
    }
    return imagesDom;
};

const Nucleus = (props) => {
    const data = get(props, 'data', props);
    const { isCMPreview } = props;
    const legalText = get(data, 'legalText', {});
    const automationId = get(data, 'automationId', '');
    const legalTarget = get(legalText, 'urlHolder.openInNewTab', false) ? '_blank' : '';
    const ctaStyle = get(data, 'styles.componentCtaStyle', 'link');
    const ctaTarget = get(data, 'cta.urlHolder.openInNewTab', false) ? '_blank' : '';
    const nucleusContainerBlock = get(NucleusConfig, `nucleusContainer.${data.componentType}`);
    const legalTextAlignment = get(data, 'styles.legalTextAlignment', 'center');
    const nucleusMargin = get(
        NucleusConfig,
        `nucleusMargin.${data?.styles?.itemStyles?.itemPadding || 'M'}`
    );

    let nucleusContainerBlockStyle = '';
    switch (nucleusContainerBlock) {
        case 'nucleus2BlockStyle':
            nucleusContainerBlockStyle = dt([
                'flex',
                'sm:flex-col',
                'md:flex-row',
                'lg:flex-row',
                'items-center',
                'justify-center',
                'self-center',
                nucleusMargin,
            ]);
            break;
        case 'nucleus3BlockStyle':
            nucleusContainerBlockStyle = dt([
                'flex',
                'sm:flex-col',
                'md:flex-row',
                'lg:flex-row',
                'items-center',
                'justify-center',
                'self-center',
                nucleusMargin,
            ]);
            break;
        case 'nucleus4BlockStyle':
            nucleusContainerBlockStyle = dt([
                'flex',
                'flex-row',
                'flex-wrap',
                'items-center',
                'justify-center',
                'self-center',
                nucleusMargin,
            ]);
            break;
        default:
            nucleusContainerBlockStyle = '';
            break;
    }
    const componentTitleColor = `#${get(data, 'styles.componentHeadlineColor', '')}`;
    const componentSubTitleColor = `#${get(data, 'styles.componentSubHeadlineColor', '')}`;
    const componentContainerBackgroundColor = `#${get(
        data,
        'styles.componentContainerBackgroundColor',
        ''
    )}`;
    const componentHeaderContainerBackgroundFillColor = `#${get(
        data,
        'styles.componentHeaderContainerBackgroundFillColor',
        ''
    )}`;

    const isBGWhite =
        componentContainerBackgroundColor === '#' ||
        componentContainerBackgroundColor === '#None' ||
        componentContainerBackgroundColor === '#ffffff' ||
        componentContainerBackgroundColor === '#fff';
    const headerExist =
        !isEmpty(data?.teaserTitle) ||
        !isEmpty(data?.teaserSubtitle) ||
        !isEmpty(data?.cta?.ctaText);

    const nucleusStyle = dt([
        'container',
        isBGWhite ? 'px-0' : '',
        'font-open-sans',
        isBGWhite && props.componentType !== 'nucleus-carousel' ? '!p-0' : '',
        !isBGWhite && props.componentType !== 'nucleus-carousel' ? 'mdOnly:px-0' : '',
        !isBGWhite && props.componentType === 'nucleus-carousel' ? 'mdOnly:px-0' : '',
        !isBGWhite ? 'smOnly:-mx-6' : '',
        !isBGWhite ? 'smMd:w-auto' : '',
        isBGWhite && props.componentType === 'nucleus-carousel' ? 'smOnly:-mx-6' : '',
        isBGWhite && props.componentType === 'nucleus-carousel' ? 'smMd:w-[calc(100%+48px)]' : '',
        isBGWhite && props.componentType === 'nucleus-carousel' ? 'mdOnly:-ml-6' : '',
        isBGWhite && props.componentType === 'nucleus-carousel' ? 'mdOnly:px-6' : '',
        ...(isBGWhite && props.componentType === 'nucleus-carousel'
            ? ['smOnly:!m-0', 'smOnly:!w-auto']
            : ['']),
        props.componentType === 'nucleus-carousel' ? 'pt-0' : 'pt-6',
        props.componentType === 'nucleus-carousel' && isBGWhite ? 'pb-0' : 'pb-4',
        !headerExist && props.componentType === 'nucleus-carousel' ? 'pt-6' : '',
    ]);
    const componentHeaderStyle = dt([
        'p-0',
        !headerExist && componentContainerBackgroundColor === '#' ? '' : 'mb-6',
        ...(headerExist && !isBGWhite && props.componentType === 'nucleus-carousel'
            ? ['py-6', 'mb-0']
            : ['']),
        ...(headerExist &&
        componentContainerBackgroundColor === '#' &&
        componentHeaderContainerBackgroundFillColor !== '#'
            ? ['!mb-4', '!p-6']
            : ['']),
        !headerExist &&
        componentContainerBackgroundColor === '#' &&
        props.componentType === 'nucleus-carousel'
            ? 'mb-0'
            : '',
        'flex',
        'flex-col',
        !isBGWhite && props.componentType !== 'nucleus-carousel' ? 'md:px-6' : '',
    ]);
    const legalTextStyle = dt([
        'text-xsmall',
        'text-black',
        `text-${legalTextAlignment}`,
        'mx-auto',
        'block',
        props.componentType === 'nucleus-carousel' ? 'mt-4' : '',
        !isBGWhite && legalTextAlignment === 'left' ? 'lg:-ml-6' : '',
        !isBGWhite && legalTextAlignment === 'right' ? 'lg:-mr-6' : '',
    ]);

    const componentHeadlineFontSize = get(data, 'styles.componentHeadlineFontSize', 'heading-4');
    const componentSubHeadlineFontSize = get(data, 'styles.componentSubHeadlineFontSize', 'body-1');

    const ComponentTitleStyle = [
        dt(['text-center', 'font-bold']),
        cmsCompHeadlineStyle[componentHeadlineFontSize] || '',
    ].join(' ');
    const componentSubTitleStyle = [
        dt(['text-center']),
        cmsCompSubHeadlineStyle[componentSubHeadlineFontSize] || '',
    ].join(' ');
    const legalTextCTAStyle = dt(['ml-1', 'underline']);
    const componentCTALinkStyle = dt([
        'mx-auto',
        'p-0',
        'underline',
        'text-base',
        'leading-6',
        'lg:text-xlarge',
        'w-auto',
        'lg:leading-7',
    ]);
    const componentCTABtnStyle = dt(['flex', 'w-60', 'last:mr-0']); // as per new mocks
    const itemPadding =
        componentHeaderContainerBackgroundFillColor === '#' ||
        props.componentType === 'nucleus-carousel'
            ? null
            : get(
                  NucleusConfig,
                  `paddingAccentColor.${data?.styles?.itemStyles?.itemPadding || 'M'}`
              );
    return (
        <div
            data-cm-metadata={isCMPreview && metaDataElement(data)}
            data-automation-id={automationId}
        >
            <div
                className={`${nucleusStyle} ${dt([
                    !isBGWhite && legalText.legalText ? 'mb-6' : '',
                ])}`}
                style={{ backgroundColor: componentContainerBackgroundColor }}
                data-automation-id="at-nucleus-main"
            >
                {data.teaserTitle || data.teaserSubtitle || data?.cta?.ctaText ? (
                    <div
                        className={[componentHeaderStyle, itemPadding].join(' ')}
                        id="componentHeader"
                        style={{ backgroundColor: componentHeaderContainerBackgroundFillColor }}
                    >
                        {data.teaserTitle ? (
                            <div
                                className={ComponentTitleStyle}
                                id="componentTitle"
                                style={{ color: componentTitleColor }}
                                data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                                data-automation-id={commonAutomationIds?.MAINTITLE}
                            >
                                {data.teaserTitle}
                            </div>
                        ) : null}
                        {data.teaserSubtitle ? (
                            <div
                                className={componentSubTitleStyle}
                                id="componentSubTitle"
                                style={{ color: componentSubTitleColor }}
                                data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                                data-automation-id={commonAutomationIds?.MAINSUBTITLE}
                            >
                                {data.teaserSubtitle}
                            </div>
                        ) : null}
                        {data?.cta?.ctaText ? (
                            <div
                                id="componentCTPaddingDiv"
                                className={
                                    ctaStyle === 'link'
                                        ? dt([
                                              'flex',
                                              'items-center',
                                              'text-center',
                                              'justify-center',
                                              'mt-6',
                                              'lg:mt-4',
                                          ])
                                        : dt(['flex', 'items-center', 'justify-center', 'mt-6'])
                                }
                            >
                                <Button
                                    automationId={ctaAutomationIds?.BUTTON}
                                    className={
                                        ctaStyle === 'link'
                                            ? componentCTALinkStyle
                                            : componentCTABtnStyle
                                    }
                                    linkUrl={data.cta.urlHolder ? data.cta.urlHolder?.value : ''}
                                    id="componentCTA"
                                    buttonType={ctaStyle === 'link' ? 'Text' : 'Secondary'}
                                    size="Lg"
                                    target={ctaTarget}
                                    type="button"
                                    style={
                                        ctaStyle === 'link'
                                            ? { color: componentSubTitleColor }
                                            : null
                                    }
                                    data-cm-metadata={isCMPreview && metaDataProperty('ctaText')}
                                    title={data.cta.ctaAltText || ''}
                                >
                                    {data.cta.ctaText}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div
                    className={[
                        nucleusContainerBlockStyle,
                        dt([
                            !isBGWhite && props.componentType !== 'nucleus-carousel'
                                ? 'mdOnly:px-6'
                                : '',
                        ]),
                    ].join(' ')}
                    data-automation-id={bannerAutomationIds?.BANNERSSECTION}
                >
                    {renderImages(data, isCMPreview)}
                </div>
                {legalText.legalText ? (
                    <div
                        className={legalTextStyle}
                        data-cm-metadata={isCMPreview && metaDataProperty('legalText')}
                        data-automation-id={commonAutomationIds?.LEGALTEXT}
                        style={{ color: componentSubTitleColor }}
                    >
                        {legalText.legalText}
                        {legalText.legalText && legalText.legalLinkText ? (
                            <a
                                aria-label="nucleus-legal-cta"
                                className={legalTextCTAStyle}
                                data-automation-id={commonAutomationIds?.LEGALLINK}
                                href={legalText.urlHolder ? legalText.urlHolder.value : ''}
                                role="link"
                                target={legalTarget}
                                title={legalText.legalAltText || ''}
                            >
                                {legalText.legalLinkText}
                            </a>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
Nucleus.defaultProps = {
    isCMPreview: false,
    componentType: '',
};

Nucleus.propTypes = {
    isCMPreview: bool,
    componentType: string,
};
export default Nucleus;
